import { PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import { Tooltip } from "@mui/material";
import { AdminGetRouteStatusesPerMonthResDto } from "service/api/estabrookSchemas";
import { DateTime } from "luxon";
import { useAppTheme } from "ui";
import {
  getRouteStateColors,
  isDateSelected,
  findMatchingDayData,
  getEffectiveState,
  hasNonPlanningState,
  shouldApplyDayStyles,
  getTooltipText,
} from "../../utils/date-picker-utils";

type CustomDayProps = {
  highlightedDays?: AdminGetRouteStatusesPerMonthResDto[];
  selectedDay?: DateTime<boolean> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & PickersDayProps<any>;

const CustomDay = ({
  highlightedDays = [],
  day,
  outsideCurrentMonth,
  selectedDay,
  ...other
}: CustomDayProps) => {
  const theme = useAppTheme();

  // Check if this day is currently selected
  const isCurrentlySelected = isDateSelected(day, selectedDay);

  // Find the matching day in highlightedDays by comparing dates
  const matchingDay = findMatchingDayData(day, highlightedDays);

  // For selected days, ignore planning state and treat as no state
  const effectiveState = getEffectiveState(matchingDay);
  const hasNonPlanningStateValue = hasNonPlanningState(
    outsideCurrentMonth,
    matchingDay,
  );

  // Determine styling based on selection and state
  const stateColors =
    isCurrentlySelected && !hasNonPlanningStateValue
      ? getRouteStateColors(theme, undefined, true) // Use default gray styling for selected days without non-planning states
      : getRouteStateColors(
          theme,
          effectiveState,
          isCurrentlySelected && hasNonPlanningStateValue,
        );

  const shouldStyle = shouldApplyDayStyles(
    outsideCurrentMonth,
    hasNonPlanningStateValue,
    isCurrentlySelected,
  );

  const tooltipText = getTooltipText(
    effectiveState,
    isCurrentlySelected,
    hasNonPlanningStateValue,
  );

  const dayComponent = (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      sx={{
        // Remove default "today" border styling
        "&.MuiPickersDay-today": {
          border: "none",
        },
        // Apply custom state styling when needed
        ...(shouldStyle && {
          border: `1px solid ${stateColors.borderColor} !important`,
          ...(stateColors.backgroundColor !== "transparent" && {
            backgroundColor: `${stateColors.backgroundColor} !important`,
          }),
          color: stateColors.color,
        }),
        ...other.sx,
      }}
    />
  );

  return tooltipText ? (
    <Tooltip
      title={tooltipText}
      placement="bottom"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -10],
              },
            },
          ],
        },
      }}
    >
      {dayComponent}
    </Tooltip>
  ) : (
    dayComponent
  );
};

export default CustomDay;
